// src/pages/setup/SetupStep3.tsx - 採用 Welcome Page3 設計模式
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { processPersonalizationFlow, markPersonalizationStatus, createOnboardingData } from '../../services/auth';
import { useAuth } from '../../contexts/AuthContext';
import { logDev } from '../../utils/logger';
import { logEvent } from '../../utils/analytics';

const SetupStep3: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshOnboardingStatus } = useAuth();
  const [answer, setAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // 從 navigation state 取得前面步驟的答案
  const prevAnswers = location.state as { q1?: string; q2?: string } | null;



  const handleComplete = async () => {
    setIsLoading(true);

    try {
      // 收集所有答案（使用 navigation state 而非 localStorage）
      const answers = {
        question1: prevAnswers?.q1 || '',
        question2: prevAnswers?.q2 || '',
        question3: answer.trim()
      };

      logDev('SetupStep3', '用戶完成所有設置步驟', {
        q1Length: answers.question1.length,
        q2Length: answers.question2.length,
        q3Length: answers.question3.length
      });

      // 追蹤：完成步驟 3
      try {
        logEvent('personalization_step_completed', { step: 3 });
      } catch (error) {
        // 靜默失敗
      }

      // 追蹤：完成所有個人化設置
      try {
        logEvent('personalization_completed');
      } catch (error) {
        // 靜默失敗
      }

      // 呼叫後端 API（樂觀更新 - 不等待結果）
      processPersonalizationFlow(answers).then((result) => {
        if (result.success) {
          logDev('SetupStep3', '個人化設置處理成功', result.data);
        } else {
          logDev('SetupStep3', '個人化設置處理失敗，但不影響用戶體驗', result.error);
        }
      }).catch((error) => {
        logDev('SetupStep3', '個人化設置 API 呼叫失敗，但不影響用戶體驗', error);
      });

      // 直接標記為完成，不需要 localStorage 清理
      await markPersonalizationStatus(true); // true = completed

      // 生成 Onboarding 種子資料（阻塞式等待）
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      logDev('SetupStep3', '準備呼叫 createOnboardingData', { timeZone: userTimeZone });

      try {
        const result = await createOnboardingData({
          timeZone: userTimeZone,
          locale: 'en'
        });

        logDev('SetupStep3', 'createOnboardingData 回應', result);

        if (result.success) {
          logDev('SetupStep3', 'Onboarding 資料創建成功', result);
        } else {
          logDev('SetupStep3', 'Onboarding 資料創建失敗', result.error);
        }
      } catch (error) {
        logDev('SetupStep3', 'createOnboardingData 拋出例外', error);
        // 失敗時仍然繼續流程
      }

      // 刷新 AuthContext 狀態，防止循環重定向
      logDev('SetupStep3', '刷新 onboarding 狀態以防循環');
      await refreshOnboardingStatus();

      // 立即導向主應用。
      navigate('/today', { replace: true });

    } catch (error) {
      logDev('SetupStep3', '處理個人化設置時發生錯誤', error);

      // 即使出錯也導向主應用，不阻擋用戶體驗
      try {
        // 錯誤情況下保守標記為未完成，讓用戶下次仍可重新設置
        await markPersonalizationStatus(false);
        await refreshOnboardingStatus();
      } catch (statusError) {
        logDev('SetupStep3', '標記狀態失敗', statusError);
      }
      navigate('/today', { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    // 回到上一步時傳遞所有已有答案
    const answers = {
      q1: prevAnswers?.q1 || '',
      q2: prevAnswers?.q2 || ''
    };
    navigate('/setup/step-2', { state: answers });
  };

  const handleSkip = async () => {
    setIsLoading(true);

    try {
      // 追蹤：跳過步驟 3
      try {
        logEvent('personalization_skipped', { step: 3 });
      } catch (error) {
        // 靜默失敗
      }

      // 收集所有答案（使用 navigation state）
      const answers = {
        question1: prevAnswers?.q1 || '',
        question2: prevAnswers?.q2 || '',
        question3: ''
      };

      // 如果至少有一個答案，就提交並標記為完成
      const hasAnyAnswer = answers.question1 || answers.question2;
      if (hasAnyAnswer) {
        processPersonalizationFlow(answers).then((result) => {
          logDev('SetupStep3', '用戶選擇跳過第三題，但提交了部分答案', result);
        }).catch((error) => {
          logDev('SetupStep3', '部分答案提交失敗', error);
        });

        // 有答案就標記為完成
        await markPersonalizationStatus(true);
      } else {
        // 沒有任何答案但用戶明確選擇跳過，標記為永久完成
        await markPersonalizationStatus(true);
      }

      // 生成 Onboarding 種子資料（阻塞式等待）
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      logDev('SetupStep3', '準備呼叫 createOnboardingData（跳過流程）', { timeZone: userTimeZone });

      try {
        const result = await createOnboardingData({
          timeZone: userTimeZone,
          locale: 'en'
        });

        logDev('SetupStep3', 'createOnboardingData 回應（跳過流程）', result);

        if (result.success) {
          logDev('SetupStep3', 'Onboarding 資料創建成功（跳過）', result);
        } else {
          logDev('SetupStep3', 'Onboarding 資料創建失敗（跳過）', result.error);
        }
      } catch (error) {
        logDev('SetupStep3', 'createOnboardingData 拋出例外（跳過流程）', error);
        // 失敗時仍然繼續流程
      }

      // 跳過時也要刷新狀態，防止循環重定向
      logDev('SetupStep3', '跳過設置，刷新 onboarding 狀態');
      await refreshOnboardingStatus();

      navigate('/today', { replace: true });

    } catch (error) {
      logDev('SetupStep3', '跳過設置時發生錯誤', error);
      navigate('/today', { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  const isValid = answer.trim().length > 0;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* 處理中的覆蓋層 */}
      {isLoading && (
        <div className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center z-loading">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-brand-blue mx-auto"></div>
            <p className="text-gray-600">Setting up your first day...</p>
            <p className="text-sm text-gray-500">This will only take a moment</p>
          </div>
        </div>
      )}

      {/* Progress Dots */}
      <div className="flex justify-center space-x-2 pt-4 pb-2">
        {Array.from({ length: 3 }, (_, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === 3;
          const isCompleted = stepNumber < 3;

          return (
            <React.Fragment key={stepNumber}>
              <div
                className={`w-2 h-2 rounded-full transition-all duration-300 ${isActive
                  ? 'bg-gradient-to-r from-sky-500 to-sky-500 transform scale-125 animate-welcome-pulse-glow'
                  : isCompleted
                    ? 'bg-gradient-to-r from-sky-500 to-sky-500'
                    : 'bg-gray-300'
                  }`}
              />
              {stepNumber < 3 && (
                <div className={`w-12 h-0.5 transition-colors duration-300 ${stepNumber < 3 ? 'bg-gradient-to-r from-sky-500 to-sky-500' : 'bg-gray-300'
                  }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-2">
        <div className="w-full max-w-sm mx-auto">
          <div className="space-y-6">
            {/* Header */}
            <div className="text-center mt-4 animate-welcome-slide-up">
              {/* Icon - 獨立視覺焦點 */}
              <div className="mb-8">
                <div className="relative w-16 h-16 mx-auto bg-gradient-to-br from-green-500 to-sky-600 rounded-2xl flex items-center justify-center">
                  <Clock className="w-8 h-8 text-white" />
                  <div className="absolute w-20 h-20 rounded-2xl border-2 border-green-400 opacity-30 animate-welcome-pulse-glow"></div>
                </div>
              </div>

              {/* 標題 + 副標題群組 - 緊密文字說明 */}
              <div className="space-y-3">
                <h2 className="text-2xl font-bold text-gray-900 animate-welcome-slide-up delay-200">
                  Any fixed & regular activities?
                </h2>
                <p className="text-gray-600 px-2 animate-welcome-slide-up delay-400">
                  Include both must-dos and routines
                </p>
              </div>
            </div>


            {/* Input Container */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 border-2 border-gray-200 animate-welcome-slide-up delay-600 opacity-0">
              <div className="relative mb-4">
                <textarea
                  placeholder="Work 9-6 on weekdays, gym around 5pm…"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  className="w-full p-4 border border-gray-200 rounded-xl resize-none focus:outline-none focus:border-green-400 focus:shadow-lg transition-all text-gray-700 placeholder-gray-400 bg-white"
                  rows={3}
                  disabled={isLoading}
                />
                <div className="absolute bottom-3 right-3 text-xs text-green-400">
                  <span className="animate-typing-blink">|</span>
                </div>
              </div>

            </div>

            {/* Buttons - 使用統一 Apple 樣式 */}
            <div className="space-y-4">
              <button
                onClick={handleComplete}
                disabled={!isValid || isLoading}
                className={`w-full py-5 px-6 rounded-xl font-semibold text-xl flex items-center justify-center space-x-2 ${isValid && !isLoading
                  ? 'btn-primary'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
              >
                <span>{isLoading ? 'Setting up...' : 'Complete Setup'}</span>
                {!isLoading && isValid && <span>→</span>}
              </button>

              <div className="flex space-x-3">
                <button
                  onClick={handleBack}
                  disabled={isLoading}
                  className="flex-1 btn-secondary py-3 px-6 rounded-xl font-medium"
                >
                  ← Back
                </button>

                <button
                  onClick={handleSkip}
                  disabled={isLoading}
                  className="flex-1 btn-secondary py-3 px-6 rounded-xl font-medium"
                >
                  Skip
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Safe Area */}
      <div className="pb-4"></div>
    </div>
  );
};

export default SetupStep3;